import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import axios from 'axios';
import { firestore } from '../config/firebase';
import { getToken } from "firebase/app-check";
import { appCheck } from '../config/firebase';

// Configure axios with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get Firebase App Check token
 * @param {boolean} forceRefresh - Force refresh the token
 * @returns {Promise<string>} App Check token
 */
const getAppCheckToken = async (forceRefresh = false) => {
  // If App Check is disabled, return null
  if (!appCheck) {
    return null;
  }

  try {
    const appCheckTokenResponse = await getToken(appCheck, forceRefresh);
    return appCheckTokenResponse.token;
  } catch (error) {
    // Silently fail and continue without App Check token
    // This allows the app to work even if App Check is throttled or misconfigured
    console.warn('App Check token unavailable, continuing without it:', error.message);
    return null;
  }
};

/**
 * Retrieve all documents from a collection
 * @param {string} collectionName - Name of the Firestore collection
 * @returns {Promise<Array>} Array of documents with their IDs
 */
export const getAllData = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(firestore, collectionName));
    const data = [];
    querySnapshot.forEach((doc) => {
      data.push({ id: doc.id, ...doc.data() });
    });
    return data;
  } catch (error) {
    console.error('Error getting documents:', error);
    throw error;
  }
};

/**
 * Retrieve a single document by ID
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - ID of the document
 * @returns {Promise<Object>} Document data with ID
 */
export const getDataById = async (collectionName, documentId) => {
  try {
    const docRef = doc(firestore, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error('Document not found');
    }
  } catch (error) {
    console.error('Error getting document:', error);
    throw error;
  }
};

/**
 * Query documents with filters
 * @param {string} collectionName - Name of the Firestore collection
 * @param {Array} filters - Array of filter objects [{field, operator, value}]
 * @param {string} orderByField - Field to order by (optional)
 * @param {number} limitCount - Number of documents to limit (optional)
 * @returns {Promise<Array>} Array of filtered documents
 */
export const queryData = async (collectionName, filters = [], orderByField = null, limitCount = null) => {
  try {
    let q = collection(firestore, collectionName);
    const constraints = [];

    // Add where clauses
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });

    // Add orderBy if specified
    if (orderByField) {
      constraints.push(orderBy(orderByField));
    }

    // Add limit if specified
    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    if (constraints.length > 0) {
      q = query(collection(firestore, collectionName), ...constraints);
    }

    const querySnapshot = await getDocs(q);
    const data = [];
    querySnapshot.forEach((doc) => {
      data.push({ id: doc.id, ...doc.data() });
    });
    return data;
  } catch (error) {
    console.error('Error querying documents:', error);
    throw error;
  }
};

/**
 * Add a new document to a collection
 * @param {string} collectionName - Name of the Firestore collection
 * @param {Object} data - Data to add
 * @returns {Promise<Object>} Added document with ID
 */
export const addData = async (collectionName, data) => {
  try {
    const docRef = await addDoc(collection(firestore, collectionName), {
      ...data,
      createdAt: new Date().toISOString()
    });

    return { id: docRef.id, ...data };
  } catch (error) {
    console.error('Error adding document:', error);
    throw error;
  }
};

/**
 * Update an existing document
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - ID of the document to update
 * @param {Object} data - Data to update
 * @returns {Promise<void>}
 */
export const updateData = async (collectionName, documentId, data) => {
  try {
    const docRef = doc(firestore, collectionName, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

/**
 * Delete a document
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - ID of the document to delete
 * @returns {Promise<void>}
 */
export const deleteData = async (collectionName, documentId) => {
  try {
    const docRef = doc(firestore, collectionName, documentId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * Check messaging/alerts for a specific network
 * @param {string} networkKey - Network key (mtn, airteltigo, telecel)
 * @returns {Promise<Object|null>}
 */
export const checkMessaging = async (networkKey) => {
  try {
    const messagingRef = doc(firestore, 'messaging', 'checkout');
    const snapshot = await getDoc(messagingRef);

    if (snapshot.exists()) {
      const { message } = snapshot.data();
      const networkMessage = message?.find(m => m.network === networkKey);
      return networkMessage || null;
    }

    return null;
  } catch (error) {
    console.error('Error checking messaging:', error);
    return null;
  }
};

/**
 * Get global alert from settings
 * @returns {Promise<Object|null>} Alert object with showAlert and message
 */
export const getGlobalAlert = async () => {
  try {
    const alertRef = doc(firestore, 'settings', 'alert');
    const snapshot = await getDoc(alertRef);

    if (snapshot.exists()) {
      const alertData = snapshot.data();
      // Return alert only if showAlert is true
      if (alertData.showAlert === true) {
        return {
          message: alertData.message,
          showAlert: alertData.showAlert
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting global alert:', error);
    return null;
  }
};

/**
 * Check if recipient exists in existing_recipients collection
 * @param {string} recipientId - Recipient phone number without formatting
 * @returns {Promise<boolean>}
 */
export const checkExistingRecipient = async (recipientId) => {
  try {
    // First check if the activation status is enabled
    const existingRef = doc(firestore, 'activations', 'existing');
    const activationSnapshot = await getDoc(existingRef);

    if (activationSnapshot.exists()) {
      const { status } = activationSnapshot.data();

      if (status) {
        // Check if recipient exists
        const recipientRef = doc(firestore, 'existing_recipients', recipientId);
        const recipientSnapshot = await getDoc(recipientRef);
        return recipientSnapshot.exists();
      }
    }

    // If activation is disabled or doesn't exist, allow all recipients
    return true;
  } catch (error) {
    console.error('Error checking existing recipient:', error);
    // On error, allow the transaction to proceed
    return true;
  }
};

/**
 * Submit data purchase order
 * @param {Object} orderData - Order data including network, volume, price, fee, recipient and momo numbers
 * @returns {Promise<Object>}
 */
export const submitDataOrder = async (orderData) => {
  try {
    // Get App Check token
    const appCheckToken = await getAppCheckToken();

    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add App Check token if available
    if (appCheckToken) {
      headers['x-firebase-appcheck'] = appCheckToken;
    }

    const response = await api.post('/checkout/paystack', orderData, { headers });
    return response.data;
  } catch (error) {
    console.error('Error submitting order:', error);
    throw error;
  }
};

/**
 * Get all data packages from networks collection
 * @returns {Promise<Array>}
 */
export const getDataPackages = async () => {
  try {
    const packages = await getAllData('dataPackages');
    return packages;
  } catch (error) {
    console.error('Error getting data packages:', error);
    throw error;
  }
};

/**
 * Get network packages from specific document path
 * @returns {Promise<Object>}
 */
export const getNetworkPackages = async () => {
  try {
    const docRef = doc(firestore, 'networks', 'networks');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      throw new Error('Network packages not found');
    }
  } catch (error) {
    console.error('Error getting network packages:', error);
    throw error;
  }
};
